import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, Wheat, Skull, TrendingUp, Plus, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function PondJournal({ pond, open, onClose }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [newL, setNewL] = useState({ date: "", avg_weight_g: "", feed_kg: "", mortality: "", water_temp: "", ph: "", oxygen_mg_l: "", notes: "" });

  async function load() {
    if (!pond) return;
    const r = await api.get(`/ponds/${pond.id}/growth`);
    setData(r.data);
  }
  useEffect(() => { if (open) load().catch(() => {}); }, [open, pond?.id]);

  async function addLog() {
    if (!newL.avg_weight_g) return toast.error("Poids moyen requis");
    try {
      await api.post("/pond_logs", {
        pond_id: pond.id, pond_name: pond.name,
        date: newL.date ? new Date(newL.date).toISOString() : new Date().toISOString(),
        avg_weight_g: parseFloat(newL.avg_weight_g || 0),
        feed_kg: parseFloat(newL.feed_kg || 0),
        mortality: parseInt(newL.mortality || 0),
        water_temp: parseFloat(newL.water_temp || 0),
        ph: newL.ph ? parseFloat(newL.ph) : null,
        oxygen_mg_l: newL.oxygen_mg_l ? parseFloat(newL.oxygen_mg_l) : null,
        notes: newL.notes,
      });
      setNewL({ date: "", avg_weight_g: "", feed_kg: "", mortality: "", water_temp: "", ph: "", oxygen_mg_l: "", notes: "" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delLog(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/pond_logs/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pond?.name} — {t("pond_journal")}</DialogTitle>
        </DialogHeader>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pond-stats">
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Scale className="w-3 h-3" />{t("latest_weight")}</div>
              <div className="text-lg font-bold mt-1">{data.latest_weight_g ?? "—"} g</div>
              {data.weight_gain_g != null && <div className="text-xs text-primary">+{data.weight_gain_g} g</div>}
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Wheat className="w-3 h-3" />{t("total_feed")}</div>
              <div className="text-lg font-bold mt-1">{data.total_feed_kg} kg</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Skull className="w-3 h-3" />{t("mortality")}</div>
              <div className="text-lg font-bold mt-1">{data.total_mortality}</div>
            </div>
            <div className="rounded-lg border border-border p-3 bg-primary/5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" />FCR</div>
              <div className="text-lg font-bold mt-1 text-primary" data-testid="pond-fcr">{data.fcr ?? "—"}</div>
              {data.biomass_gain_kg != null && <div className="text-xs text-muted-foreground">+{data.biomass_gain_kg} kg biomasse</div>}
            </div>
          </div>
        )}

        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" />{t("add_log")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>{t("date")}</Label><Input type="date" data-testid="log-date" value={newL.date} onChange={(e) => setNewL({ ...newL, date: e.target.value })} /></div>
            <div><Label>{t("weight_g")}</Label><Input type="number" step="0.1" data-testid="log-weight" value={newL.avg_weight_g} onChange={(e) => setNewL({ ...newL, avg_weight_g: e.target.value })} /></div>
            <div><Label>{t("feed_kg")}</Label><Input type="number" step="0.1" data-testid="log-feed" value={newL.feed_kg} onChange={(e) => setNewL({ ...newL, feed_kg: e.target.value })} /></div>
            <div><Label>{t("mortality")}</Label><Input type="number" data-testid="log-mortality" value={newL.mortality} onChange={(e) => setNewL({ ...newL, mortality: e.target.value })} /></div>
            <div><Label>{t("water_temp")}</Label><Input type="number" step="0.1" value={newL.water_temp} onChange={(e) => setNewL({ ...newL, water_temp: e.target.value })} /></div>
            <div><Label>pH</Label><Input type="number" step="0.1" data-testid="log-ph" value={newL.ph} onChange={(e) => setNewL({ ...newL, ph: e.target.value })} /></div>
            <div><Label>{t("oxygen")}</Label><Input type="number" step="0.1" data-testid="log-oxygen" value={newL.oxygen_mg_l} onChange={(e) => setNewL({ ...newL, oxygen_mg_l: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={newL.notes} onChange={(e) => setNewL({ ...newL, notes: e.target.value })} /></div>
          </div>
          <Button size="sm" onClick={addLog} data-testid="log-save">{t("save")}</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border">
              <tr>
                <th className="py-2">{t("date")}</th>
                <th>{t("weight_g")}</th>
                <th>{t("feed_kg")}</th>
                <th>{t("mortality")}</th>
                <th>{t("water_temp")}</th>
                <th>pH</th>
                <th>O₂</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.logs || []).map((l) => (
                <tr key={l.id} className="border-b border-border/60 last:border-0">
                  <td className="py-2">{formatDate(l.date, lang)}</td>
                  <td className="font-medium">{l.avg_weight_g} g</td>
                  <td>{l.feed_kg} kg</td>
                  <td>{l.mortality}</td>
                  <td>{l.water_temp}°C</td>
                  <td>{l.ph || "—"}</td>
                  <td className={l.oxygen_mg_l && l.oxygen_mg_l < 5 ? "text-destructive font-semibold" : ""}>{l.oxygen_mg_l ? `${l.oxygen_mg_l} mg/L` : "—"}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => delLog(l.id)} data-testid={`log-del-${l.id}`}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
